/**
 * Module-level guard that ensures `ChatProvider` calls
 * `requestNewRoomId` exactly once per visitor identity. The flag is
 * stored on `window` (with a SSR fallback) instead of React state so it
 * survives even if `ChatProvider` itself ever remounts (e.g. a consumer
 * that doesn't opt into `keepMountedWhenHidden`, or a future regression
 * in `hasBeenOpened`) — under the current widget wiring, `ChatProvider`
 * mounts once — on the widget's first open, not on page load — and stays
 * mounted afterward, so this mostly guards
 * against double-invocation (React StrictMode, multiple Widget
 * instances) rather than a collapse/reopen cycle. The only legitimate
 * reset point is a visitor identity change — `TenantUserIdProvider`
 * invokes `resetInitialRoomRequested` as part of its visitor-scoped
 * cleanup.
 *
 * Known limitation, not addressed here: if the initial `requestNewRoomId()`
 * call fails, the flag is already set and nothing resets it, so `roomId`
 * stays `undefined` for the rest of the session — same failure mode as
 * before this guard's introduction.
 *
 * Living in its own neutral module breaks the circular dependency
 * between `chat.tsx` (consumes the guard, plus React Query hooks
 * that depend on the tenant context) and `tenant-user-id-provider.tsx`
 * (resets the guard on tenant change).
 */
const INITIAL_ROOM_REQUEST_FLAG = "__FIINDER_INITIAL_ROOM_REQUESTED__";
let initialRoomRequestedFallback = false;

/**
 * Reads the guard. Returns `true` when a `ChatProvider` mount has
 * already requested the initial room for the current visitor.
 */
export function hasRequestedInitialRoom(): boolean {
  if (typeof window !== "undefined") {
    return Boolean((window as Record<string, unknown>)[INITIAL_ROOM_REQUEST_FLAG]);
  }
  return initialRoomRequestedFallback;
}

/**
 * Sets the guard. Called by `ChatProvider`'s initial-mount effect
 * right before dispatching `handleNewRoom()` so a subsequent mount
 * (caused by collapse/reopen) does not double-request.
 */
export function markInitialRoomRequested(): void {
  if (typeof window !== "undefined") {
    (window as Record<string, unknown>)[INITIAL_ROOM_REQUEST_FLAG] = true;
  } else {
    initialRoomRequestedFallback = true;
  }
}

/**
 * Clears the guard so the next `ChatProvider` mount is allowed to
 * call `handleNewRoom()` again. The only legitimate caller is
 * `TenantUserIdProvider`, as part of its visitor-scoped cleanup
 * on tenant identity change.
 */
export function resetInitialRoomRequested(): void {
  if (typeof window !== "undefined") {
    delete (window as Record<string, unknown>)[INITIAL_ROOM_REQUEST_FLAG];
  }
  initialRoomRequestedFallback = false;
}
